import { SerializedSkipCategoryPreference } from './ParentalControlsService'

export interface PreferenceNode {
  preference: SerializedSkipCategoryPreference
  children: PreferenceNode[]
}

export type MuteStatus = 'muted' | 'unmuted' | 'mixed'

export function buildPreferenceTree(preferences: SerializedSkipCategoryPreference[]): PreferenceNode[] {
  const nodeMap = new Map<number, PreferenceNode>()
  const roots: PreferenceNode[] = []

  for (const pref of preferences) {
    nodeMap.set(pref.id, { preference: pref, children: [] })
  }

  for (const pref of preferences) {
    const node = nodeMap.get(pref.id)
    if (!node) continue
    if (pref.parentId == null) {
      roots.push(node)
    } else {
      const parent = nodeMap.get(pref.parentId)
      if (parent) {
        parent.children.push(node)
      }
    }
  }

  return roots
}

export function getAllNonLeafIds(nodes: PreferenceNode[]): number[] {
  const ids: number[] = []
  for (const node of nodes) {
    if (node.children.length > 0) {
      ids.push(node.preference.id)
      ids.push(...getAllNonLeafIds(node.children))
    }
  }
  return ids
}

/**
 * Count leaf nodes (the actual toggleable items) from a flat preference list.
 * A leaf is a preference whose `id` never appears as another preference's `parentId`.
 */
export function countLeaves(preferences: { id: number; parentId?: number | null }[]): number {
  const parentIds = new Set(preferences.map((p) => p.parentId).filter((id) => id != null))
  return preferences.filter((p) => !parentIds.has(p.id)).length
}

/**
 * Count skipped leaf nodes from a flat preference list.
 */
export function countSkippedLeaves(
  preferences: { id: number; parentId?: number | null; skipped?: boolean | null }[],
): number {
  const parentIds = new Set(preferences.map((p) => p.parentId).filter((id) => id != null))
  return preferences.filter((p) => !parentIds.has(p.id) && p.skipped).length
}

/**
 * Flatten a preference tree depth-first, preserving hierarchical order.
 */
export function flattenPreferenceTree(nodes: PreferenceNode[]): SerializedSkipCategoryPreference[] {
  const result: SerializedSkipCategoryPreference[] = []
  for (const node of nodes) {
    result.push(node.preference)
    if (node.children.length > 0) {
      result.push(...flattenPreferenceTree(node.children))
    }
  }
  return result
}

/**
 * Build a Map from child ID to parent ID for O(1) ancestor lookups.
 */
export function buildParentMap(preferences: { id: number; parentId?: number | null }[]): Map<number, number> {
  const map = new Map<number, number>()
  for (const p of preferences) {
    if (p.parentId != null) {
      map.set(p.id, p.parentId)
    }
  }
  return map
}

/**
 * Get all ancestor IDs (parent, grandparent, etc.) using a pre-computed parent map.
 * O(1) per level with cycle protection.
 */
export function getAncestorIdsFromMap(prefId: number, parentMap: Map<number, number>): number[] {
  const ancestors: number[] = []
  const visited = new Set<number>()
  let currentId = prefId
  while (parentMap.has(currentId)) {
    const parentId = parentMap.get(currentId)!
    if (visited.has(parentId)) break
    visited.add(parentId)
    ancestors.push(parentId)
    currentId = parentId
  }
  return ancestors
}

/**
 * Build a Map from parent ID to child IDs for O(1) lookups.
 */
export function buildChildrenMap(preferences: { id: number; parentId?: number | null }[]): Map<number, number[]> {
  const map = new Map<number, number[]>()
  for (const p of preferences) {
    if (p.parentId != null) {
      const children = map.get(p.parentId)
      if (children) {
        children.push(p.id)
      } else {
        map.set(p.parentId, [p.id])
      }
    }
  }
  return map
}

/**
 * Get all descendant IDs using a pre-computed children map (O(n) total instead of O(n²)).
 */
export function getDescendantIdsFromMap(
  nodeId: number,
  childrenMap: Map<number, number[]>,
  visited: Set<number> = new Set(),
): number[] {
  const children = childrenMap.get(nodeId)
  if (!children) return []
  const ids: number[] = []
  for (const childId of children) {
    if (visited.has(childId)) continue
    visited.add(childId)
    ids.push(childId)
    ids.push(...getDescendantIdsFromMap(childId, childrenMap, visited))
  }
  return ids
}

/**
 * Get leaf descendant IDs of a node using a pre-computed children map.
 * A leaf is a node that has no children in the map.
 */
export function getLeafDescendantIds(
  nodeId: number,
  childrenMap: Map<number, number[]>,
  visited: Set<number> = new Set(),
): number[] {
  const children = childrenMap.get(nodeId)
  if (!children) return []
  const leaves: number[] = []
  for (const childId of children) {
    if (visited.has(childId)) continue
    visited.add(childId)
    if (!childrenMap.has(childId)) {
      leaves.push(childId)
    } else {
      leaves.push(...getLeafDescendantIds(childId, childrenMap, visited))
    }
  }
  return leaves
}

/**
 * Sum totalCount and skippedCount from root-level preferences (those with no parentId).
 * These fields reflect the true totals from the API, even when filtered by contentId.
 */
export function sumRootCounts(preferences: { parentId?: number | null; totalCount: number; skippedCount: number }[]): {
  total: number
  skipped: number
} {
  let total = 0
  let skipped = 0
  for (const p of preferences) {
    if (p.parentId == null) {
      total += p.totalCount
      skipped += p.skippedCount
    }
  }
  return { total, skipped }
}

export function getMuteStatus(node: PreferenceNode): MuteStatus {
  if (node.children.length === 0) {
    return node.preference.skipped ? 'muted' : 'unmuted'
  }
  const { skippedCount, totalCount } = node.preference
  if (skippedCount === totalCount && totalCount > 0) return 'muted'
  if (skippedCount === 0) return 'unmuted'
  return 'mixed'
}
